import { useCallback, useEffect, useRef } from 'react';
import { projectApi } from '@/api/project.ts';
import { useStudioStore } from '@/store/useStudioStore';
import { useChatStore } from '@/store/useChatStore';
import { useCreatorStore } from '@/store/useCreatorStore.ts';
import { getExpertReviewSummary, useExpertStore } from '@/store/useExpertStore.ts';
import { userApi } from '@/api/user.ts';
import { toast } from 'sonner';
import {
  getExpertResponseFormat,
  getCreatorResponseFormat,
  parseMaybeJsonContent,
} from '@/lib/agentChatUtils.ts';
import {
  ExpertReviewResponse,
  CreationSuggestionsResponse,
  CreationSuggestionItem,
} from '@/types/protocol';
import type { WebpageDraft } from '@/types/project';
import {
  buildDraftPreviewHtml,
  extractMetadataFromHtml,
  normalizeGeneratedHtml,
  stripLinkerScript,
} from '@/lib/domUtils';
import {
  cleanupSupersededPendingDrafts,
  rememberRecentDraftStatus,
  resolveEditableDraft,
  TECHNICAL_GENERATION_SYNC_CHANGE_NOTE,
} from '@/lib/draftUtils';
import { detectUnexpectedGlobalLanguageDrift } from '@/lib/selectedElementEdit';
import { normalizeUnexpectedResponseMessage } from '@/lib/utils';
import { trackEvent } from '@/api/track';
import { trackProductSalesEvent } from '@/lib/create/productSalesTracking';
import {
  trackStudioExpertMessageResult,
  type StudioExpertMessageTrackingMeta,
} from '@/lib/studioTracking';
import {
  buildModifyGenerateRequestConfig,
  PRODUCT_PAGE_GENERATION_TASK_CODE,
  STANDARD_WEBPAGE_GENERATE_REQUEST_CONFIG,
  type GenerateWebpageRequestConfig,
} from '@/lib/webpageGeneration';
import { isAiCreditsError } from '@/lib/creditsGuard';

interface GenerateProjectReviewCallbacks {
  sourceMessageId?: string | null;
  reviewTitle?: string;
  reviewNote?: string;
  onReady?: (data: {
    draftId: string;
    version: number | null;
    beforeHtml: string;
    afterHtml: string;
  }) => void;
  onError?: (msg: string) => void;
}

type GenerateError = Error & { code?: string };

const createGenerateError = (message: string, code: string): GenerateError => {
  const error = new Error(message) as GenerateError;
  error.code = code;
  return error;
};

const GENERATION_HEARTBEAT_MS = 60_000;
const GENERATION_RECOVERY_LOOKBACK_MS = 60_000;
const GENERATION_DISCONNECT_RETRY_DELAY_MS = 60_000;
const GENERATION_FINAL_RECOVERY_TIMEOUT_MS = 120_000;
const GENERATION_FINAL_RECOVERY_POLL_MS = 5_000;

const buildPreviewHtmlFromDraft = (html: string) => buildDraftPreviewHtml(html);

const PRODUCT_STREAM_RECOVERY_CHANGE_NOTE =
  'Recovered product page HTML from interrupted generation stream.';

const isProductPageGenerationRequest = (requestConfig: GenerateWebpageRequestConfig) =>
  requestConfig.flowOptions?.generateTaskCode === PRODUCT_PAGE_GENERATION_TASK_CODE;

const getCompleteHtmlFromStream = (content: string) => {
  const normalized = normalizeGeneratedHtml(content)
    .replace(/\s*```\s*$/g, '')
    .trim();
  if (!normalized) return '';

  const htmlEndIndex = normalized.toLowerCase().lastIndexOf('</html>');
  if (htmlEndIndex < 0) return '';

  const completeHtml = normalized.slice(0, htmlEndIndex + '</html>'.length).trim();
  return /<html[\s>]/i.test(completeHtml) ? stripLinkerScript(completeHtml) : '';
};

type StreamAttemptState = {
  isCompleted: boolean;
  hasReceivedData: boolean;
  isCancelled: boolean;
  hasTimedOut: boolean;
};

const RECOVERABLE_STREAM_ERROR_CODES = new Set([
  'INTERRUPTED',
  'FAILED',
  'STREAM_TIMEOUT',
  'AbortError',
]);

const resolveStreamFailureCode = (attemptState: StreamAttemptState, code?: string) => {
  const normalizedCode = code?.trim();
  if (normalizedCode) return normalizedCode;
  if (attemptState.hasTimedOut) return 'STREAM_TIMEOUT';
  return attemptState.hasReceivedData ? 'INTERRUPTED' : 'FAILED';
};

const isRecoverableStreamFailure = (code?: string) =>
  Boolean(code && RECOVERABLE_STREAM_ERROR_CODES.has(code));

const GENERATED_DRAFT_FETCH_RETRY_COUNT = 5;
const GENERATED_DRAFT_FETCH_RETRY_DELAY_MS = 500;
const GENERATED_DRAFT_LIST_PAGE_SIZE = 10;

const waitForGeneratedDraftRetry = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

const waitForFinalRecoveryWindow = async (
  recover: () => Promise<boolean>,
  shouldContinue: () => boolean,
  logMessage: string,
) => {
  const deadline = Date.now() + GENERATION_FINAL_RECOVERY_TIMEOUT_MS;

  while (shouldContinue()) {
    try {
      if (await recover()) return true;
    } catch (error) {
      console.warn(logMessage, error);
    }

    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) break;
    await waitForGeneratedDraftRetry(Math.min(GENERATION_FINAL_RECOVERY_POLL_MS, remainingMs));
  }

  return false;
};

const isDraftWithContent = (draft: WebpageDraft | null): draft is WebpageDraft =>
  Boolean(draft?.content);

const sortDraftsByVersionDesc = (drafts: WebpageDraft[]) =>
  [...drafts].sort((a, b) => {
    if (b.version !== a.version) return b.version - a.version;
    return new Date(b.createTime || 0).getTime() - new Date(a.createTime || 0).getTime();
  });

const findGeneratedDraft = (
  drafts: WebpageDraft[],
  expectedDraftId: string,
  expectedVersion: number | null,
) =>
  drafts.find((draft) => {
    if (expectedDraftId && String(draft.id) !== expectedDraftId) return false;
    if (expectedVersion != null && draft.version !== expectedVersion) return false;
    return true;
  }) || null;

const loadGeneratedDraftAfterDone = async (
  userProjectId: number,
  draftId?: string | number | null,
  version?: string | number | null,
): Promise<WebpageDraft> => {
  const expectedDraftId = String(draftId ?? '').trim();
  const parsedVersion = Number(version);
  const expectedVersion = Number.isFinite(parsedVersion) ? parsedVersion : null;

  for (let attempt = 0; attempt < GENERATED_DRAFT_FETCH_RETRY_COUNT; attempt += 1) {
    const [pendingDraft, confirmedDraft, latestAnyDraft, draftPage] = await Promise.all([
      projectApi.getLatestDraft(userProjectId, ['PENDING']),
      projectApi.getLatestDraft(userProjectId, ['CONFIRMED']),
      projectApi.getLatestDraft(userProjectId),
      projectApi.listDrafts({
        userProjectId,
        pageNumber: 1,
        pageSize: GENERATED_DRAFT_LIST_PAGE_SIZE,
        draftStatuses: ['PENDING', 'CONFIRMED'],
      }),
    ]);
    const draftsWithContent = [pendingDraft, confirmedDraft, latestAnyDraft].filter(
      isDraftWithContent,
    );

    const matchedDraft = findGeneratedDraft(draftsWithContent, expectedDraftId, expectedVersion);
    if (matchedDraft) return matchedDraft;

    if (!expectedDraftId && expectedVersion == null) {
      const newestDraft = sortDraftsByVersionDesc(draftsWithContent)[0];
      if (newestDraft) return newestDraft;
    }

    const expectedDraftExistsInList =
      Boolean(expectedDraftId || expectedVersion != null) &&
      draftPage.list?.some((draft) => {
        if (expectedDraftId && String(draft.id) !== expectedDraftId) return false;
        if (expectedVersion != null && draft.version !== expectedVersion) return false;
        return true;
      });

    if (expectedDraftExistsInList) {
      console.warn('[useSSEGenerate] Generated draft exists but content is not ready yet.', {
        expectedDraftId,
        expectedVersion,
      });
    }

    if (attempt < GENERATED_DRAFT_FETCH_RETRY_COUNT - 1) {
      await waitForGeneratedDraftRetry(GENERATED_DRAFT_FETCH_RETRY_DELAY_MS);
    }
  }

  throw createGenerateError(
    'The generated draft was saved but could not be loaded. Please retry.',
    'DRAFT_NOT_FOUND',
  );
};

const getDraftTimestamp = (draft?: Pick<WebpageDraft, 'createTime'> | null) => {
  if (!draft?.createTime) return Number.NaN;
  return new Date(draft.createTime).getTime();
};

const isRecoverableDraft = (
  draft: WebpageDraft | null,
  baselineVersion: number | null,
  baselineDraftId: number | string | null,
  startedAt: number,
) => {
  if (!draft?.content) return false;
  if (baselineDraftId != null && String(draft.id) === String(baselineDraftId)) {
    return false;
  }
  if (baselineVersion != null) {
    return draft.version > baselineVersion;
  }

  const createdAt = getDraftTimestamp(draft);
  if (Number.isNaN(createdAt)) return true;
  return createdAt >= startedAt - GENERATION_RECOVERY_LOOKBACK_MS;
};

/**
 * Handles AI webpage generation, draft persistence, and review-state transitions.
 */
export const useSSEGenerate = () => {
  const {
    setIsBuildDone,
    setIsBuilding,
    setGeneratedHtml,
    setStudioMode,
    setCurrentDraftMeta,
    setPendingCommand,
    setPendingGenerateRequestConfig,
    setPreviewApplying,
    startPreviewReview,
    currentId,
    studioMode,
  } = useStudioStore();
  const { setIsGenerating, setBuildError, clearMessages } = useChatStore();
  const {
    setReviewing,
    setOverallText,
    setItems,
    setActiveIndex,
    setProjectId: setExpertProjectId,
    completeLatestReviewSession,
    failLatestReviewSession,
  } = useExpertStore();
  const {
    setCreating,
    setWordsItems,
    setLookItems,
    setPartsItems,
    setImpeccableItems,
    setInfoItems,
    setProjectId: setCreatorProjectId,
    setEditing,
  } = useCreatorStore();
  const activeStreamControllerRef = useRef<AbortController | null>(null);
  const activeHeartbeatRef = useRef<number | null>(null);
  const activeDisconnectRetryRef = useRef<number | null>(null);
  const activeAttemptRef = useRef<StreamAttemptState | null>(null);
  const unmountCleanupTimerRef = useRef<number | null>(null);

  const clearHeartbeat = useCallback(() => {
    if (activeHeartbeatRef.current != null) {
      window.clearTimeout(activeHeartbeatRef.current);
      activeHeartbeatRef.current = null;
    }
  }, []);

  const clearDisconnectRetry = useCallback(() => {
    if (activeDisconnectRetryRef.current != null) {
      window.clearTimeout(activeDisconnectRetryRef.current);
      activeDisconnectRetryRef.current = null;
    }
  }, []);

  const cancelActiveStream = useCallback(
    (markCancelled = true) => {
      if (markCancelled && activeAttemptRef.current) {
        activeAttemptRef.current.isCancelled = true;
      }
      clearHeartbeat();
      clearDisconnectRetry();
      setPreviewApplying(false);
      if (activeStreamControllerRef.current) {
        try {
          activeStreamControllerRef.current.abort();
        } catch (error) {
          console.warn('[useSSEGenerate] Failed to abort active stream:', error);
        } finally {
          activeStreamControllerRef.current = null;
        }
      }
    },
    [clearDisconnectRetry, clearHeartbeat, setPreviewApplying],
  );

  useEffect(() => {
    if (unmountCleanupTimerRef.current != null) {
      window.clearTimeout(unmountCleanupTimerRef.current);
      unmountCleanupTimerRef.current = null;
    }

    return () => {
      if (unmountCleanupTimerRef.current != null) {
        window.clearTimeout(unmountCleanupTimerRef.current);
      }
      unmountCleanupTimerRef.current = window.setTimeout(() => {
        unmountCleanupTimerRef.current = null;
        cancelActiveStream(true);
      }, 100);
    };
  }, [cancelActiveStream]);

  const getDisplayError = useCallback((msg?: string, code?: string) => {
    if (code === 'INSUFFICIENT_POINTS') {
      return 'Insufficient credits to generate this page. Please top up and try again.';
    }
    if (code === 'DRAFT_EXISTS') {
      return 'A pending draft already exists. Review or resolve it before sending another request.';
    }
    if (code === 'INTERRUPTED' || code === 'STREAM_TIMEOUT') {
      return 'The generation was interrupted before completion. Please retry.';
    }
    if (code === 'FAILED') {
      return 'The connection failed before the generation could finish. Please retry.';
    }
    if (code === 'TRUNCATED') {
      return 'The output was truncated. Try simplifying the request and generate again.';
    }
    return normalizeUnexpectedResponseMessage(
      msg,
      'AI generation failed due to model error, network abnormality, or other issues.',
    );
  }, []);

  const finalizeSeoAndTrack = useCallback(
    (processedHtml: string, startTime: number) => {
      const generationTime = Math.floor((Date.now() - startTime) / 1000);

      const metadata = extractMetadataFromHtml(processedHtml);
      projectApi
        .saveSeoConfig(
          {
            userProjectId: Number(currentId),
            title: metadata.title,
            description: metadata.description,
            keywords: metadata.keywords,
          },
          { suppressErrorToast: true },
        )
        .catch((error) => {
          console.warn('[useSSEGenerate] Failed to auto-save SEO config:', error);
        });

      trackEvent({
        eventCode: 'app_gen_success',
        eventData: JSON.stringify({ generation_time: generationTime }),
      });
    },
    [currentId],
  );

  const handleGenerateFailure = useCallback(
    (
      msg: string | undefined,
      code: string | undefined,
      onErrorCallback?: (msg: string) => void,
    ) => {
      console.error('[useSSEGenerate] SSE generation failed:', code, msg);
      clearDisconnectRetry();
      setPreviewApplying(false);
      setIsGenerating(false);
      setCreating(false);
      setEditing(false);
      setReviewing(false);
      const displayMsg = getDisplayError(msg, code);
      if (onErrorCallback) {
        onErrorCallback(displayMsg);
      } else {
        setBuildError(true, displayMsg);
      }
    },
    [
      getDisplayError,
      clearDisconnectRetry,
      setBuildError,
      setCreating,
      setEditing,
      setIsGenerating,
      setPreviewApplying,
      setReviewing,
    ],
  );

  const requestExpertReview = useCallback(
    async (htmlTemplate?: string, reviewFocusPrompt?: string) => {
      setReviewing(true);
      const targetProjectId = currentId ?? useStudioStore.getState().currentProjectId ?? null;
      if (currentId) setExpertProjectId(currentId);
      const latestGeneratedHtml = htmlTemplate ?? useStudioStore.getState().generatedHtml;
      const trimmedReviewFocusPrompt = reviewFocusPrompt?.trim();
      const reviewFocusInstruction = trimmedReviewFocusPrompt
        ? ` focusPrompt:${trimmedReviewFocusPrompt}.`
        : '';

      userApi
        .aiGenerateStream(
          {
            taskCode: 'EXPERT_REVIEW',
            message: `${getExpertResponseFormat()}.${reviewFocusInstruction} htmlTemplate:${latestGeneratedHtml}`,
          },
          undefined,
          { showCreditsModal: false },
        )
        .then((res) => {
          console.log('妫ｅ啯鐣?~  ~ [ExpertReview]: ', res);

          const { cleanText, parsed } = parseMaybeJsonContent(res?.content ?? '');

          const nextOverallText = parsed
            ? (parsed as unknown as ExpertReviewResponse).overall_text || ''
            : cleanText?.trim() || '';
          const nextSuggestions = parsed
            ? (parsed as unknown as ExpertReviewResponse).suggestions || []
            : [];

          const mappedSuggestions = nextSuggestions.map((item) => ({
            ...item,
            icon: item.typeName,
          }));

          setOverallText(nextOverallText);
          setItems(mappedSuggestions);
          setActiveIndex(mappedSuggestions.length > 0 ? 0 : -1);
          completeLatestReviewSession({
            projectId: targetProjectId,
            summary: getExpertReviewSummary(nextOverallText, mappedSuggestions),
            suggestionCount: mappedSuggestions.length,
          });

          if (parsed) {
            toast.success('Plan generated, preparing your brief...');
          }
        })
        .catch((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          failLatestReviewSession(targetProjectId, errorMessage);
          toast.error(errorMessage);
        })
        .finally(() => {
          setReviewing(false);
        });
    },
    [
      currentId,
      setReviewing,
      setOverallText,
      setExpertProjectId,
      setItems,
      setActiveIndex,
      completeLatestReviewSession,
      failLatestReviewSession,
    ],
  );

  const triggerAutoExpertReviewAfterInitialBuild = useCallback(
    (htmlTemplate: string) => {
      const trimmedHtml = htmlTemplate.trim();
      if (!trimmedHtml) return;

      const studioState = useStudioStore.getState();
      const targetProjectId = currentId ?? studioState.currentProjectId ?? null;
      if (!targetProjectId) return;

      const expertStore = useExpertStore.getState();
      expertStore.setProjectId(targetProjectId);
      expertStore.setItems([]);
      expertStore.setReviewing(true);
      expertStore.setActiveIndex(-1);
      expertStore.setOverallText('');
      expertStore.startReviewSession(targetProjectId);

      void requestExpertReview(trimmedHtml);
    },
    [currentId, requestExpertReview],
  );

  const markGenerationStart = useCallback(
    (command: string, requestConfig: GenerateWebpageRequestConfig) => {
      clearMessages();
      setPendingCommand(command);
      setPendingGenerateRequestConfig(requestConfig);
      setIsBuilding(true);
      setBuildError(false, null);
      setIsBuildDone(false);
      setIsGenerating(true);
      clearHeartbeat();
      clearDisconnectRetry();
    },
    [
      clearDisconnectRetry,
      clearHeartbeat,
      clearMessages,
      setBuildError,
      setIsBuildDone,
      setIsBuilding,
      setIsGenerating,
      setPendingCommand,
      setPendingGenerateRequestConfig,
    ],
  );

  const recoverBuildFromLatestDraft = useCallback(
    async (
      startedAt: number,
      baselineVersion: number | null,
      baselineDraftId: number | string | null,
      shouldAutoRunInitialExpertReview: boolean,
      callback?: () => void,
    ) => {
      const userProjectId = Number(currentId);
      if (!Number.isFinite(userProjectId) || userProjectId <= 0) {
        return false;
      }

      const [pendingDraft, confirmedDraft] = await Promise.all([
        projectApi.getLatestDraft(userProjectId, ['PENDING']),
        projectApi.getLatestDraft(userProjectId, ['CONFIRMED']),
      ]);

      const pendingCandidate = isRecoverableDraft(
        pendingDraft,
        baselineVersion,
        baselineDraftId,
        startedAt,
      )
        ? pendingDraft
        : null;
      const confirmedCandidate = isRecoverableDraft(
        confirmedDraft,
        baselineVersion,
        baselineDraftId,
        startedAt,
      )
        ? confirmedDraft
        : null;

      const recoveredDraft =
        confirmedCandidate && pendingCandidate
          ? confirmedCandidate.version >= pendingCandidate.version
            ? confirmedCandidate
            : pendingCandidate
          : confirmedCandidate || pendingCandidate;

      if (!recoveredDraft?.content) {
        return false;
      }

      let recoveredStatus = recoveredDraft.draftStatus;
      if (recoveredStatus === 'PENDING') {
        try {
          await projectApi.updateDraftStatus({
            draftId: recoveredDraft.id,
            draftStatus: 'CONFIRMED',
          });
          recoveredStatus = 'CONFIRMED';
        } catch (error) {
          console.warn('[useSSEGenerate] Failed to auto-confirm recovered draft:', error);
        }
      }

      const processedHtml = buildPreviewHtmlFromDraft(recoveredDraft.content);
      finalizeSeoAndTrack(processedHtml, startedAt);
      setGeneratedHtml(processedHtml);
      setCurrentDraftMeta(recoveredDraft.id, recoveredDraft.version, recoveredStatus);
      setIsBuildDone(true);
      setPendingCommand(null);
      setCreating(false);
      setEditing(false);
      setIsGenerating(false);
      setPreviewApplying(false);
      setReviewing(false);

      if (studioMode === '') {
        setStudioMode('expert');
      }

      if (shouldAutoRunInitialExpertReview) {
        triggerAutoExpertReviewAfterInitialBuild(processedHtml);
      }

      callback?.();
      return true;
    },
    [
      currentId,
      finalizeSeoAndTrack,
      setCurrentDraftMeta,
      setGeneratedHtml,
      setIsBuildDone,
      setPendingCommand,
      setCreating,
      setEditing,
      setIsGenerating,
      setPreviewApplying,
      setReviewing,
      setStudioMode,
      studioMode,
      triggerAutoExpertReviewAfterInitialBuild,
    ],
  );

  const recoverReviewDraft = useCallback(
    async (
      startedAt: number,
      baselineVersion: number | null,
      baselineDraftId: number | string | null,
      beforeHtml: string,
      callbacks?: GenerateProjectReviewCallbacks,
    ) => {
      const userProjectId = Number(currentId);
      if (!Number.isFinite(userProjectId) || userProjectId <= 0) {
        return false;
      }

      const pendingDraft = await projectApi.getLatestDraft(userProjectId, ['PENDING']);
      if (
        !isRecoverableDraft(pendingDraft, baselineVersion, baselineDraftId, startedAt) ||
        !pendingDraft?.content
      ) {
        return false;
      }

      const afterHtml = buildPreviewHtmlFromDraft(pendingDraft.content);
      finalizeSeoAndTrack(afterHtml, startedAt);
      startPreviewReview({
        draftId: pendingDraft.id,
        version: pendingDraft.version,
        beforeHtml: beforeHtml || afterHtml,
        afterHtml,
        title: callbacks?.reviewTitle || 'Review latest change',
        note:
          callbacks?.reviewNote ||
          'Switch between the current draft and the proposed edit, then decide which version to keep.',
        sourceMessageId: callbacks?.sourceMessageId || null,
      });

      setCreating(false);
      setEditing(false);
      setIsGenerating(false);
      setPreviewApplying(false);
      setReviewing(false);

      callbacks?.onReady?.({
        draftId: String(pendingDraft.id),
        version: pendingDraft.version,
        beforeHtml: beforeHtml || afterHtml,
        afterHtml,
      });

      return true;
    },
    [
      currentId,
      finalizeSeoAndTrack,
      setCreating,
      setEditing,
      setIsGenerating,
      setPreviewApplying,
      setReviewing,
      startPreviewReview,
    ],
  );

  const ensureDraftReadyForGeneration = useCallback(async () => {
    const userProjectId = Number(currentId);
    if (!Number.isFinite(userProjectId) || userProjectId <= 0) {
      throw createGenerateError('Missing project context.', 'INVALID_PROJECT');
    }

    const state = useStudioStore.getState();
    if (state.previewReview.active) {
      throw createGenerateError(
        'Review the latest proposed edit before sending another request.',
        'DRAFT_EXISTS',
      );
    }

    const pendingDraft = await projectApi.getLatestDraft(userProjectId, ['PENDING']);
    if (!pendingDraft) return;

    const currentState = useStudioStore.getState();
    const isCurrentPendingDraft =
      currentState.currentDraftStatus === 'PENDING' &&
      currentState.currentDraftId != null &&
      String(currentState.currentDraftId) === String(pendingDraft.id);

    if (isCurrentPendingDraft) {
      await projectApi.updateDraftStatus({
        draftId: pendingDraft.id,
        draftStatus: 'CONFIRMED',
      });
      setCurrentDraftMeta(pendingDraft.id, pendingDraft.version, 'CONFIRMED');
      return;
    }

    const confirmedDraft = await projectApi.getLatestDraft(userProjectId, ['CONFIRMED']);
    if (
      pendingDraft.content &&
      confirmedDraft?.content &&
      pendingDraft.version <= confirmedDraft.version
    ) {
      const clearedDraftIds = await cleanupSupersededPendingDrafts(
        userProjectId,
        confirmedDraft.version,
      );

      if (clearedDraftIds.includes(pendingDraft.id)) {
        return;
      }
    }

    if (
      pendingDraft.content &&
      confirmedDraft?.content &&
      pendingDraft.version > confirmedDraft.version
    ) {
      const beforeHtml = buildPreviewHtmlFromDraft(confirmedDraft.content);
      const afterHtml = buildPreviewHtmlFromDraft(pendingDraft.content);

      setGeneratedHtml(beforeHtml);
      setCurrentDraftMeta(confirmedDraft.id, confirmedDraft.version, confirmedDraft.draftStatus);
      startPreviewReview({
        draftId: pendingDraft.id,
        version: pendingDraft.version,
        beforeHtml,
        afterHtml,
        title: 'Review latest change',
        note: 'Switch between the current draft and the proposed edit, then decide which version to keep.',
        sourceMessageId: null,
      });

      throw createGenerateError(
        'A newer draft is waiting for review. Review it before sending another request.',
        'DRAFT_EXISTS',
      );
    }

    throw createGenerateError(
      'A pending draft already exists. Review or resolve it before sending another request.',
      'DRAFT_EXISTS',
    );
  }, [currentId, setCurrentDraftMeta, setGeneratedHtml, startPreviewReview]);

  const syncCurrentGeneratedHtmlToDraft = useCallback(async () => {
    const userProjectId = Number(currentId);
    if (!Number.isFinite(userProjectId) || userProjectId <= 0) {
      return;
    }

    const currentHtml = stripLinkerScript(useStudioStore.getState().generatedHtml || '');
    if (!currentHtml) {
      return;
    }

    const { editableDraft, pendingReview, saveBaseVersion } =
      await resolveEditableDraft(userProjectId);

    if (pendingReview || !editableDraft?.content) {
      return;
    }

    if (stripLinkerScript(editableDraft.content) === currentHtml) {
      return;
    }

    const syncedDraft = await projectApi.saveDraft(
      {
        userProjectId,
        content: currentHtml,
        changeNote: TECHNICAL_GENERATION_SYNC_CHANGE_NOTE,
        draftStatus: 'CONFIRMED',
        baseVersion: saveBaseVersion ?? editableDraft.version,
      },
      { suppressErrorToast: true },
    );

    setCurrentDraftMeta(syncedDraft.id, syncedDraft.version, syncedDraft.draftStatus);
  }, [currentId, setCurrentDraftMeta]);

  const generateProject = useCallback(
    async (
      command: string,
      callback?: () => void,
      onErrorCallback?: (msg: string) => void,
      requestConfig: GenerateWebpageRequestConfig = STANDARD_WEBPAGE_GENERATE_REQUEST_CONFIG,
    ) => {
      const userProjectId = Number(currentId);
      const initialState = useStudioStore.getState();
      const shouldAutoRunInitialExpertReview =
        initialState.currentDraftId == null &&
        initialState.currentDraftVersion == null &&
        !initialState.generatedHtml.trim();
      let truncatedRetryCount = 0;
      let timeoutRetryCount = 0;
      let disconnectRetryCount = 0;
      let baselineVersion: number | null = null;
      let baselineDraftId: number | string | null = null;
      let startTime = 0;
      let streamedContent = '';
      const isProductGeneration = isProductPageGenerationRequest(requestConfig);
      const attemptState: StreamAttemptState = {
        isCompleted: false,
        hasReceivedData: false,
        isCancelled: false,
        hasTimedOut: false,
      };

      cancelActiveStream(true);
      activeAttemptRef.current = attemptState;
      markGenerationStart(command, requestConfig);

      const getRetryCount = () => truncatedRetryCount + timeoutRetryCount + disconnectRetryCount;

      const trackProductGenerationSuccess = (
        generationResultType: string,
        recovered: boolean,
        recoveryType: string,
        draftId?: string | number | null,
        draftVersion?: string | number | null,
      ) => {
        if (!isProductGeneration) return;
        trackProductSalesEvent('product_sales_generation_success', {
          durationMs: startTime ? Date.now() - startTime : 0,
          projectId: useStudioStore.getState().currentProjectId || '',
          userProjectId: String(userProjectId || ''),
          draftId: draftId != null ? String(draftId) : '',
          draftVersion: draftVersion != null ? String(draftVersion) : '',
          generationResultType,
          flowType: requestConfig.flowType || '',
          generateTaskCode: String(requestConfig.flowOptions?.generateTaskCode || ''),
          retryCount: getRetryCount(),
          recovered,
          recoveryType,
        });
      };

      const trackProductGenerationFailure = (
        failureReason: string,
        errorCode?: string,
        extra?: {
          displayMessage?: string;
          errorType?: string;
          retryable?: boolean;
          hasReceivedData?: boolean;
          hasPartialHtml?: boolean;
        },
      ) => {
        if (!isProductGeneration) return;
        const resolvedErrorCode = errorCode || '';
        trackProductSalesEvent('product_sales_generation_failure', {
          durationMs: startTime ? Date.now() - startTime : 0,
          projectId: useStudioStore.getState().currentProjectId || '',
          userProjectId: String(userProjectId || ''),
          flowType: requestConfig.flowType || '',
          generateTaskCode: String(requestConfig.flowOptions?.generateTaskCode || ''),
          retryCount: getRetryCount(),
          recovered: false,
          errorCode: resolvedErrorCode,
          errorType: extra?.errorType || resolvedErrorCode || 'generation_failure',
          failureReason,
          displayMessage:
            extra?.displayMessage || getDisplayError(failureReason, resolvedErrorCode),
          hasReceivedData: extra?.hasReceivedData ?? attemptState.hasReceivedData,
          hasPartialHtml:
            extra?.hasPartialHtml ?? Boolean(getCompleteHtmlFromStream(streamedContent)),
          retryable: extra?.retryable ?? isRecoverableStreamFailure(resolvedErrorCode),
        });
      };

      try {
        await syncCurrentGeneratedHtmlToDraft();
        const baselineState = useStudioStore.getState();
        baselineVersion = baselineState.currentDraftVersion ?? null;
        baselineDraftId = baselineState.currentDraftId ?? null;
        startTime = Date.now();
        await ensureDraftReadyForGeneration();
      } catch (error) {
        const err = error as GenerateError;
        activeAttemptRef.current = null;
        trackProductGenerationFailure(err.message, err.code, {
          displayMessage: getDisplayError(err.message, err.code),
          retryable: false,
        });
        handleGenerateFailure(err.message, err.code, onErrorCallback);
        return;
      }

      const scheduleHeartbeatTimeout = () => {
        clearHeartbeat();
        activeHeartbeatRef.current = window.setTimeout(() => {
          if (attemptState.isCompleted || attemptState.isCancelled) return;
          attemptState.hasTimedOut = true;
          activeStreamControllerRef.current?.abort();
        }, GENERATION_HEARTBEAT_MS);
      };

      const resetAttemptForRetry = () => {
        attemptState.hasTimedOut = false;
        attemptState.hasReceivedData = false;
        streamedContent = '';
      };

      const recoverProductBuildFromStream = async (code?: string) => {
        if (!isProductPageGenerationRequest(requestConfig)) {
          return false;
        }
        if (code !== 'GENERATE_FAILED' && code !== 'INTERRUPTED') {
          return false;
        }

        const recoveredHtml = getCompleteHtmlFromStream(streamedContent);
        if (!recoveredHtml) {
          return false;
        }

        try {
          const savedDraft = await projectApi.saveDraft(
            {
              userProjectId,
              content: recoveredHtml,
              contentType: 'HTML',
              changeNote: PRODUCT_STREAM_RECOVERY_CHANGE_NOTE,
              draftStatus: 'CONFIRMED',
              baseVersion: baselineVersion ?? undefined,
            },
            { suppressErrorToast: true },
          );

          const processedHtml = buildPreviewHtmlFromDraft(savedDraft.content || recoveredHtml);
          finalizeSeoAndTrack(processedHtml, startTime);
          setGeneratedHtml(processedHtml);
          setCurrentDraftMeta(savedDraft.id, savedDraft.version, savedDraft.draftStatus);
          setIsBuildDone(true);
          setPendingCommand(null);
          setCreating(false);
          setEditing(false);
          setIsGenerating(false);
          setPreviewApplying(false);
          setReviewing(false);

          if (studioMode === '') {
            setStudioMode('expert');
          }

          if (shouldAutoRunInitialExpertReview) {
            triggerAutoExpertReviewAfterInitialBuild(processedHtml);
          }

          trackProductGenerationSuccess(
            'product_stream_recovery',
            true,
            'stream_complete_html',
            savedDraft.id,
            savedDraft.version,
          );
          callback?.();
          return true;
        } catch (error) {
          console.warn('[useSSEGenerate] Failed to recover product HTML from stream:', error);
          return false;
        }
      };

      const scheduleDisconnectRetry = () => {
        clearHeartbeat();
        clearDisconnectRetry();
        activeDisconnectRetryRef.current = window.setTimeout(async () => {
          activeDisconnectRetryRef.current = null;
          if (attemptState.isCompleted || attemptState.isCancelled) return;

          try {
            const recovered = await recoverBuildFromLatestDraft(
              startTime,
              baselineVersion,
              baselineDraftId,
              shouldAutoRunInitialExpertReview,
              callback,
            );
            if (recovered) {
              attemptState.isCompleted = true;
              activeAttemptRef.current = null;
              const recoveredState = useStudioStore.getState();
              trackProductGenerationSuccess(
                'draft_recovery_after_disconnect_delay',
                true,
                'latest_draft',
                recoveredState.currentDraftId,
                recoveredState.currentDraftVersion,
              );
              return;
            }
          } catch (error) {
            console.warn(
              '[useSSEGenerate] Failed to recover build before delayed disconnect retry:',
              error,
            );
          }

          resetAttemptForRetry();
          startGenerate();
        }, GENERATION_DISCONNECT_RETRY_DELAY_MS);
      };

      const startGenerate = () => {
        clearDisconnectRetry();
        scheduleHeartbeatTimeout();

        activeStreamControllerRef.current = projectApi.generateWebpageStream(
          {
            userProjectId,
            message: command,
            ...requestConfig,
          },
          {
            onDelta: (content) => {
              attemptState.hasReceivedData = true;
              streamedContent += content;
              scheduleHeartbeatTimeout();
            },
            onRetrying: () => {
              attemptState.hasReceivedData = false;
              streamedContent = '';
              scheduleHeartbeatTimeout();
            },
            onHeartbeat: () => {
              scheduleHeartbeatTimeout();
            },
            onDone: async ({ draftId, version }) => {
              attemptState.isCompleted = true;
              clearHeartbeat();
              clearDisconnectRetry();
              activeStreamControllerRef.current = null;
              activeAttemptRef.current = null;

              try {
                const generatedDraft = await loadGeneratedDraftAfterDone(
                  userProjectId,
                  draftId,
                  version,
                );
                const processedHtml = buildPreviewHtmlFromDraft(generatedDraft.content);
                finalizeSeoAndTrack(processedHtml, startTime);

                let draftStatus = generatedDraft.draftStatus;
                if (draftStatus === 'PENDING') {
                  try {
                    await projectApi.updateDraftStatus({
                      draftId: generatedDraft.id,
                      draftStatus: 'CONFIRMED',
                    });
                    draftStatus = 'CONFIRMED';
                  } catch (err) {
                    console.error('[useSSEGenerate] Failed to auto-confirm generated draft:', err);
                  }
                }

                setGeneratedHtml(processedHtml);
                setCurrentDraftMeta(generatedDraft.id, generatedDraft.version, draftStatus);
                setIsBuildDone(true);
                setPendingCommand(null);
                setCreating(false);
                setEditing(false);
                setIsGenerating(false);
                setPreviewApplying(false);
                setReviewing(false);

                if (studioMode === '') {
                  setStudioMode('expert');
                }

                if (shouldAutoRunInitialExpertReview) {
                  triggerAutoExpertReviewAfterInitialBuild(processedHtml);
                }

                trackProductGenerationSuccess(
                  'sse_done',
                  false,
                  '',
                  generatedDraft.id,
                  generatedDraft.version,
                );
                callback?.();
              } catch (error) {
                const err = error as GenerateError;
                trackProductGenerationFailure(err.message, err.code, {
                  displayMessage: getDisplayError(err.message, err.code),
                  errorType: err.code || 'draft_load_failure',
                  retryable: false,
                });
                handleGenerateFailure(err.message, err.code, onErrorCallback);
              }
            },
            onTruncated: () => {
              clearHeartbeat();
              clearDisconnectRetry();
              activeStreamControllerRef.current = null;
              if (truncatedRetryCount === 0) {
                truncatedRetryCount += 1;
                resetAttemptForRetry();
                startGenerate();
              } else {
                activeAttemptRef.current = null;
                trackProductGenerationFailure(
                  'AI generation failed due to model error, network abnormality, or other issues.',
                  'TRUNCATED',
                  {
                    displayMessage: getDisplayError(
                      'AI generation failed due to model error, network abnormality, or other issues.',
                      'TRUNCATED',
                    ),
                    errorType: 'TRUNCATED',
                    retryable: false,
                    hasPartialHtml: Boolean(getCompleteHtmlFromStream(streamedContent)),
                  },
                );
                handleGenerateFailure(
                  'AI generation failed due to model error, network abnormality, or other issues.',
                  'TRUNCATED',
                  onErrorCallback,
                );
              }
            },
            onError: async (msg, code) => {
              clearHeartbeat();
              activeStreamControllerRef.current = null;

              if (attemptState.isCancelled) {
                clearDisconnectRetry();
                activeAttemptRef.current = null;
                return;
              }

              const resolvedCode = resolveStreamFailureCode(attemptState, code);

              if (attemptState.hasTimedOut && timeoutRetryCount === 0) {
                clearDisconnectRetry();
                timeoutRetryCount += 1;
                resetAttemptForRetry();
                startGenerate();
                return;
              }

              const shouldDelayRetryForDisconnect =
                !attemptState.hasTimedOut &&
                disconnectRetryCount === 0 &&
                isRecoverableStreamFailure(resolvedCode);

              if (shouldDelayRetryForDisconnect) {
                disconnectRetryCount += 1;
                scheduleDisconnectRetry();
                return;
              }

              const shouldRecover = isRecoverableStreamFailure(resolvedCode);

              if (shouldRecover) {
                try {
                  const recovered = await recoverBuildFromLatestDraft(
                    startTime,
                    baselineVersion,
                    baselineDraftId,
                    shouldAutoRunInitialExpertReview,
                    callback,
                  );
                  if (recovered) {
                    attemptState.isCompleted = true;
                    activeAttemptRef.current = null;
                    const recoveredState = useStudioStore.getState();
                    trackProductGenerationSuccess(
                      'draft_recovery_after_disconnect',
                      true,
                      'latest_draft',
                      recoveredState.currentDraftId,
                      recoveredState.currentDraftVersion,
                    );
                    return;
                  }
                } catch (error) {
                  console.warn('[useSSEGenerate] Failed to recover build after disconnect:', error);
                }
              }

              if (await recoverProductBuildFromStream(resolvedCode)) {
                attemptState.isCompleted = true;
                activeAttemptRef.current = null;
                return;
              }

              if (shouldRecover) {
                const recoveredDuringFinalCheck = await waitForFinalRecoveryWindow(
                  () =>
                    recoverBuildFromLatestDraft(
                      startTime,
                      baselineVersion,
                      baselineDraftId,
                      shouldAutoRunInitialExpertReview,
                      callback,
                    ),
                  () => !attemptState.isCompleted && !attemptState.isCancelled,
                  '[useSSEGenerate] Failed to recover build while confirming final failure:',
                );

                if (recoveredDuringFinalCheck) {
                  attemptState.isCompleted = true;
                  activeAttemptRef.current = null;
                  const recoveredState = useStudioStore.getState();
                  trackProductGenerationSuccess(
                    'draft_recovery_final_check',
                    true,
                    'latest_draft',
                    recoveredState.currentDraftId,
                    recoveredState.currentDraftVersion,
                  );
                  return;
                }
              }

              activeAttemptRef.current = null;
              trackProductGenerationFailure(msg, resolvedCode, {
                displayMessage: getDisplayError(msg, resolvedCode),
                errorType: resolvedCode,
                retryable: false,
                hasReceivedData: attemptState.hasReceivedData,
                hasPartialHtml: Boolean(getCompleteHtmlFromStream(streamedContent)),
              });
              handleGenerateFailure(msg, resolvedCode, onErrorCallback);
            },
          },
        );
      };

      startGenerate();
    },
    [
      currentId,
      studioMode,
      setIsBuildDone,
      setGeneratedHtml,
      setStudioMode,
      setIsGenerating,
      setPendingCommand,
      setPreviewApplying,
      setCreating,
      setEditing,
      finalizeSeoAndTrack,
      handleGenerateFailure,
      clearHeartbeat,
      clearDisconnectRetry,
      cancelActiveStream,
      markGenerationStart,
      recoverBuildFromLatestDraft,
      setCurrentDraftMeta,
      setReviewing,
      ensureDraftReadyForGeneration,
      syncCurrentGeneratedHtmlToDraft,
      triggerAutoExpertReviewAfterInitialBuild,
      getDisplayError,
    ],
  );

  const generateProjectForReview = useCallback(
    async (
      command: string,
      callbacks?: GenerateProjectReviewCallbacks,
      requestConfig: GenerateWebpageRequestConfig = buildModifyGenerateRequestConfig(),
    ) => {
      const userProjectId = Number(currentId);
      const beforeHtml = useStudioStore.getState().generatedHtml;
      let truncatedRetryCount = 0;
      let timeoutRetryCount = 0;
      let disconnectRetryCount = 0;
      let baselineVersion: number | null = null;
      let baselineDraftId: number | string | null = null;
      let startTime = 0;
      const attemptState: StreamAttemptState = {
        isCompleted: false,
        hasReceivedData: false,
        isCancelled: false,
        hasTimedOut: false,
      };

      cancelActiveStream(true);
      activeAttemptRef.current = attemptState;
      setPreviewApplying(true);
      setBuildError(false, null);
      setIsGenerating(true);
      clearHeartbeat();

      try {
        await syncCurrentGeneratedHtmlToDraft();
        const baselineState = useStudioStore.getState();
        baselineVersion = baselineState.currentDraftVersion ?? null;
        baselineDraftId = baselineState.currentDraftId ?? null;
        startTime = Date.now();
        await ensureDraftReadyForGeneration();
      } catch (error) {
        const err = error as GenerateError;
        activeAttemptRef.current = null;
        handleGenerateFailure(err.message, err.code, callbacks?.onError);
        return;
      }

      const scheduleHeartbeatTimeout = () => {
        clearHeartbeat();
        activeHeartbeatRef.current = window.setTimeout(() => {
          if (attemptState.isCompleted || attemptState.isCancelled) return;
          attemptState.hasTimedOut = true;
          activeStreamControllerRef.current?.abort();
        }, GENERATION_HEARTBEAT_MS);
      };

      const resetAttemptForRetry = () => {
        attemptState.hasTimedOut = false;
        attemptState.hasReceivedData = false;
      };

      const scheduleDisconnectRetry = () => {
        clearHeartbeat();
        clearDisconnectRetry();
        activeDisconnectRetryRef.current = window.setTimeout(async () => {
          activeDisconnectRetryRef.current = null;
          if (attemptState.isCompleted || attemptState.isCancelled) return;

          try {
            const recovered = await recoverReviewDraft(
              startTime,
              baselineVersion,
              baselineDraftId,
              beforeHtml,
              callbacks,
            );
            if (recovered) {
              attemptState.isCompleted = true;
              activeAttemptRef.current = null;
              return;
            }
          } catch (error) {
            console.warn(
              '[useSSEGenerate] Failed to recover review draft before delayed disconnect retry:',
              error,
            );
          }

          resetAttemptForRetry();
          startGenerate();
        }, GENERATION_DISCONNECT_RETRY_DELAY_MS);
      };

      const startGenerate = () => {
        clearDisconnectRetry();
        scheduleHeartbeatTimeout();

        activeStreamControllerRef.current = projectApi.generateWebpageStream(
          {
            userProjectId,
            message: command,
            ...requestConfig,
          },
          {
            onDelta: () => {
              attemptState.hasReceivedData = true;
              scheduleHeartbeatTimeout();
            },
            onRetrying: () => {
              attemptState.hasReceivedData = false;
              scheduleHeartbeatTimeout();
            },
            onHeartbeat: () => {
              scheduleHeartbeatTimeout();
            },
            onDone: async ({ draftId, version }) => {
              attemptState.isCompleted = true;
              clearHeartbeat();
              clearDisconnectRetry();
              activeStreamControllerRef.current = null;
              activeAttemptRef.current = null;

              try {
                const generatedDraft = await loadGeneratedDraftAfterDone(
                  userProjectId,
                  draftId,
                  version,
                );
                const afterHtml = buildPreviewHtmlFromDraft(generatedDraft.content);
                const unexpectedLanguageDrift = detectUnexpectedGlobalLanguageDrift(
                  command,
                  beforeHtml,
                  afterHtml,
                );

                if (unexpectedLanguageDrift) {
                  if (generatedDraft.id) {
                    try {
                      await projectApi.updateDraftStatus({
                        draftId: generatedDraft.id,
                        draftStatus: 'ABANDONED',
                      });
                      rememberRecentDraftStatus(generatedDraft.id, 'ABANDONED');
                    } catch (error) {
                      console.warn(
                        '[useSSEGenerate] Failed to discard drifted selected-element draft:',
                        error,
                      );
                    }
                  }

                  handleGenerateFailure(
                    `The AI changed too much outside the selected element and flipped the page language from ${unexpectedLanguageDrift.beforeLanguage} to ${unexpectedLanguageDrift.afterLanguage}. I discarded that proposed draft. Please retry with a more specific local instruction.`,
                    'TARGETED_EDIT_DRIFT',
                    callbacks?.onError,
                  );
                  return;
                }

                finalizeSeoAndTrack(afterHtml, startTime);

                startPreviewReview({
                  draftId: generatedDraft.id,
                  version: generatedDraft.version,
                  beforeHtml: beforeHtml || afterHtml,
                  afterHtml,
                  title: callbacks?.reviewTitle || 'Review latest change',
                  note:
                    callbacks?.reviewNote ||
                    'Switch between the current draft and the proposed edit, then decide which version to keep.',
                  sourceMessageId: callbacks?.sourceMessageId || null,
                });

                setCreating(false);
                setEditing(false);
                setIsGenerating(false);
                setPreviewApplying(false);
                setReviewing(false);

                callbacks?.onReady?.({
                  draftId: String(generatedDraft.id),
                  version: generatedDraft.version,
                  beforeHtml: beforeHtml || afterHtml,
                  afterHtml,
                });
              } catch (error) {
                const err = error as GenerateError;
                handleGenerateFailure(err.message, err.code, callbacks?.onError);
              }
            },
            onTruncated: () => {
              clearHeartbeat();
              clearDisconnectRetry();
              activeStreamControllerRef.current = null;
              if (truncatedRetryCount === 0) {
                truncatedRetryCount += 1;
                resetAttemptForRetry();
                startGenerate();
              } else {
                activeAttemptRef.current = null;
                handleGenerateFailure(
                  'AI generation failed due to model error, network abnormality, or other issues.',
                  'TRUNCATED',
                  callbacks?.onError,
                );
              }
            },
            onError: async (msg, code) => {
              clearHeartbeat();
              activeStreamControllerRef.current = null;

              if (attemptState.isCancelled) {
                clearDisconnectRetry();
                activeAttemptRef.current = null;
                return;
              }

              const resolvedCode = resolveStreamFailureCode(attemptState, code);

              if (attemptState.hasTimedOut && timeoutRetryCount === 0) {
                clearDisconnectRetry();
                timeoutRetryCount += 1;
                resetAttemptForRetry();
                startGenerate();
                return;
              }

              const shouldDelayRetryForDisconnect =
                !attemptState.hasTimedOut &&
                disconnectRetryCount === 0 &&
                isRecoverableStreamFailure(resolvedCode);

              if (shouldDelayRetryForDisconnect) {
                disconnectRetryCount += 1;
                scheduleDisconnectRetry();
                return;
              }

              const shouldRecover = isRecoverableStreamFailure(resolvedCode);

              if (shouldRecover) {
                try {
                  const recovered = await recoverReviewDraft(
                    startTime,
                    baselineVersion,
                    baselineDraftId,
                    beforeHtml,
                    callbacks,
                  );
                  if (recovered) {
                    attemptState.isCompleted = true;
                    activeAttemptRef.current = null;
                    return;
                  }
                } catch (error) {
                  console.warn('[useSSEGenerate] Failed to recover review draft:', error);
                }
              }

              if (shouldRecover) {
                const recoveredDuringFinalCheck = await waitForFinalRecoveryWindow(
                  () =>
                    recoverReviewDraft(
                      startTime,
                      baselineVersion,
                      baselineDraftId,
                      beforeHtml,
                      callbacks,
                    ),
                  () => !attemptState.isCompleted && !attemptState.isCancelled,
                  '[useSSEGenerate] Failed to recover review draft while confirming final failure:',
                );

                if (recoveredDuringFinalCheck) {
                  attemptState.isCompleted = true;
                  activeAttemptRef.current = null;
                  return;
                }
              }

              activeAttemptRef.current = null;
              handleGenerateFailure(msg, resolvedCode, callbacks?.onError);
            },
          },
        );
      };

      startGenerate();
    },
    [
      currentId,
      finalizeSeoAndTrack,
      handleGenerateFailure,
      clearHeartbeat,
      clearDisconnectRetry,
      cancelActiveStream,
      recoverReviewDraft,
      setCreating,
      setBuildError,
      setEditing,
      setIsGenerating,
      setPreviewApplying,
      setReviewing,
      startPreviewReview,
      ensureDraftReadyForGeneration,
      syncCurrentGeneratedHtmlToDraft,
    ],
  );

  // Fetch expert review suggestions.
  const getExpertReview = useCallback(
    async (
      htmlTemplate?: string,
      reviewFocusPrompt?: string,
      trackingMeta?: StudioExpertMessageTrackingMeta,
    ) => {
      setReviewing(true);
      const targetProjectId = currentId ?? useStudioStore.getState().currentProjectId ?? null;
      if (currentId) setExpertProjectId(currentId);
      const latestGeneratedHtml = htmlTemplate ?? useStudioStore.getState().generatedHtml;
      const trimmedReviewFocusPrompt = reviewFocusPrompt?.trim();
      const reviewFocusInstruction = trimmedReviewFocusPrompt
        ? ` focusPrompt:${trimmedReviewFocusPrompt}.`
        : '';

      userApi
        .aiGenerateStream(
          {
            taskCode: 'EXPERT_REVIEW',
            message: `${getExpertResponseFormat()}.${reviewFocusInstruction} htmlTemplate:${latestGeneratedHtml}`,
          },
          undefined,
          {
            showCreditsModal: true,
            creditsModalCopy:
              "You don't have enough credits to generate an expert review. Top up your account to continue.",
            creditsModalProductArea: 'studio',
            creditsModalSurface: 'studio_expert_review',
            creditsModalStage: 'expert_review_generation',
            creditsModalTrigger: 'generate',
            creditsModalUiType: 'modal',
            creditsModalSource: 'studio_expert_panel',
            creditsModalOperation: 'expert_review_generation',
            creditsModalMetadata: {
              project_id: targetProjectId,
              has_review_focus_prompt: Boolean(trimmedReviewFocusPrompt),
            },
          },
        )
        .then((res) => {
          console.log('棣冩畬 ~  ~ [ExpertReview]: ', res);

          const { cleanText, parsed } = parseMaybeJsonContent(res?.content ?? '');

          const nextOverallText = parsed
            ? (parsed as unknown as ExpertReviewResponse).overall_text || ''
            : cleanText?.trim() || '';
          const nextSuggestions = parsed
            ? (parsed as unknown as ExpertReviewResponse).suggestions || []
            : [];

          const mappedSuggestions = nextSuggestions.map((item) => ({
            ...item,
            icon: item.typeName,
          }));

          setOverallText(nextOverallText);
          setItems(mappedSuggestions);
          setActiveIndex(mappedSuggestions.length > 0 ? 0 : -1);
          completeLatestReviewSession({
            projectId: targetProjectId,
            summary: getExpertReviewSummary(nextOverallText, mappedSuggestions),
            suggestionCount: mappedSuggestions.length,
          });
          trackStudioExpertMessageResult(trackingMeta, {
            result: 'success',
            hasPendingReview: false,
            suggestionCount: mappedSuggestions.length,
          });

          if (parsed) {
            toast.success('Plan generated, preparing your brief...');
          }
        })
        .catch((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          trackStudioExpertMessageResult(trackingMeta, {
            result: 'failure',
            hasPendingReview: false,
            failStage: 'expert_review',
            errorCode: error instanceof Error ? error.name : errorMessage || 'expert_review_failed',
          });
          failLatestReviewSession(targetProjectId, errorMessage);
          toast.error(errorMessage);
        })
        .finally(() => {
          setReviewing(false);
        });
    },
    [
      currentId,
      setReviewing,
      setOverallText,
      setExpertProjectId,
      setItems,
      setActiveIndex,
      completeLatestReviewSession,
      failLatestReviewSession,
    ],
  );

  // Fetch creator suggestions.
  const getCreationSuggestions = useCallback(
    async (htmlTemplate?: string) => {
      setCreating(true);
      if (currentId) setCreatorProjectId(currentId);
      const latestGeneratedHtml = htmlTemplate ?? useStudioStore.getState().generatedHtml;

      userApi
        .aiGenerateStream(
          {
            taskCode: 'CREATOR_ASSISTANT',
            message: `${getCreatorResponseFormat()}. htmlTemplate:${latestGeneratedHtml}`,
          },
          undefined,
          {
            showCreditsModal: true,
            creditsModalCopy:
              "You don't have enough credits to generate creation suggestions. Top up your account to continue.",
            creditsModalProductArea: 'studio',
            creditsModalSurface: 'studio_creation_suggestions',
            creditsModalStage: 'quick_edit_suggestion_generation',
            creditsModalTrigger: 'generate',
            creditsModalUiType: 'modal',
            creditsModalSource: 'studio_expert_panel',
            creditsModalOperation: 'creation_suggestions_generation',
            creditsModalMetadata: {
              project_id: currentId,
            },
          },
        )
        .then((res) => {
          console.log('棣冩畬 ~  ~ [CreationSuggestions]: ', res);

          const { parsed } = parseMaybeJsonContent(res?.content ?? '');

          if (parsed) {
            const creationData = parsed as unknown as CreationSuggestionsResponse;

            const mapItems = (items?: CreationSuggestionItem[]) =>
              items?.map((item) => ({
                ...item,
                id: crypto.randomUUID(),
              })) || [];

            if (creationData.words) setWordsItems(mapItems(creationData.words));
            if (creationData.look) setLookItems(mapItems(creationData.look));
            if (creationData.parts) setPartsItems(mapItems(creationData.parts));
            if (creationData.impeccable) setImpeccableItems(mapItems(creationData.impeccable));
            if (creationData.info) setInfoItems(mapItems(creationData.info));

            if (currentId) setCreatorProjectId(currentId);

            toast.success('Creation suggestions generated successfully');
          }
        })
        .catch((error: unknown) => {
          if (isAiCreditsError(error)) return;
          const errorMessage = error instanceof Error ? error.message : String(error);
          toast.error(errorMessage);
        })
        .finally(() => {
          setCreating(false);
        });
    },
    [
      setCreating,
      setWordsItems,
      setLookItems,
      setPartsItems,
      setImpeccableItems,
      setInfoItems,
      currentId,
      setCreatorProjectId,
    ],
  );

  return { generateProject, generateProjectForReview, getExpertReview, getCreationSuggestions };
};
